import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiRequest } from '@/lib/api';
import type {
  AdminEntry,
  AdminProject,
  AdminProjectDetail,
  Entry,
  EntryPage,
  EntryRules,
  PersonalProject,
  PersonalProjectDetail,
  PersonalProjects,
} from '@/types/projects';

const ENTRY_PAGE_SIZE = 50;
export const ALL_ENTRY_RULES: EntryRules = { manualEntriesAllowed: true, selfEditAllowed: true };

function entriesUrl(base: string, cursor: string | null, extra: Record<string, string | undefined> = {}): string {
  const params = new URLSearchParams({ limit: String(ENTRY_PAGE_SIZE) });
  if (cursor) params.set('cursor', cursor);
  for (const [k, v] of Object.entries(extra)) if (v) params.set(k, v);
  return `${base}?${params.toString()}`;
}

// ── personal ────────────────────────────────────────────────────────────────

/** The loggable projects plus the No-project rules (sprint 11); select picks what a caller needs. */
function useProjectsQuery<T>(select: (data: PersonalProjects) => T): UseQueryResult<T> {
  return useQuery({ queryKey: ['projects'], queryFn: () => apiRequest<PersonalProjects>('/api/projects'), select });
}

export function useProjects(): UseQueryResult<PersonalProject[]> {
  return useProjectsQuery((d) => d.items);
}

/** Sprint 11 (WP-10): the entry rules for "No project". */
export function useNoProjectRules(): UseQueryResult<EntryRules> {
  // A payload without noProject (an older server during a rolling deploy) allows everything, as before.
  return useProjectsQuery((d) => d.noProject?.rules ?? ALL_ENTRY_RULES);
}

/** 404 (archived or missing) is rendered as Not found, so no global toast. */
export function useProject(id: string, enabled: boolean): UseQueryResult<PersonalProjectDetail> {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => apiRequest<PersonalProjectDetail>(`/api/projects/${id}`),
    enabled,
    retry: false,
    meta: { silent: true },
  });
}

export function useProjectEntries(id: string, enabled: boolean): UseInfiniteQueryResult<InfiniteData<EntryPage<Entry>>> {
  return useInfiniteQuery({
    queryKey: ['projects', id, 'entries'],
    queryFn: ({ pageParam }) => apiRequest<EntryPage<Entry>>(entriesUrl(`/api/projects/${id}/entries`, pageParam)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  });
}

// ── admin ───────────────────────────────────────────────────────────────────

export function useAdminProjects(status: 'active' | 'archived', enabled = true): UseQueryResult<AdminProject[]> {
  return useQuery({
    queryKey: ['admin', 'projects', status],
    queryFn: async () => (await apiRequest<{ items: AdminProject[] }>(`/api/admin/projects?status=${status}`)).items,
    enabled,
  });
}

export function useAdminProject(id: string, enabled: boolean): UseQueryResult<AdminProjectDetail> {
  return useQuery({
    queryKey: ['admin', 'projects', id],
    queryFn: () => apiRequest<AdminProjectDetail>(`/api/admin/projects/${id}`),
    enabled,
    retry: false,
    meta: { silent: true },
  });
}

export function useAdminProjectEntries(
  id: string,
  userId: string | undefined,
  enabled: boolean,
): UseInfiniteQueryResult<InfiniteData<EntryPage<AdminEntry>>> {
  return useInfiniteQuery({
    queryKey: ['admin', 'projects', id, 'entries', userId ?? 'all'],
    queryFn: ({ pageParam }) => apiRequest<EntryPage<AdminEntry>>(entriesUrl(`/api/admin/projects/${id}/entries`, pageParam, { userId })),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  });
}

function useInvalidateProjects(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['projects'] }),
      queryClient.invalidateQueries({ queryKey: ['admin', 'projects'] }),
    ]);
  };
}

export interface ProjectInput {
  name: string;
  color: string;
  /** Sprint 11: create only (default Standard). */
  templateId?: string;
}

export function useSaveProject(): UseMutationResult<{ project: AdminProject }, Error, ProjectInput & { id?: string }> {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({ id, ...body }) =>
      id
        ? apiRequest<{ project: AdminProject }>(`/api/admin/projects/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
        : apiRequest<{ project: AdminProject }>('/api/admin/projects', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: invalidate,
  });
}

export function useProjectStatusAction(): UseMutationResult<{ project: AdminProject }, Error, { id: string; action: 'archive' | 'restore' }> {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: ({ id, action }) => apiRequest<{ project: AdminProject }>(`/api/admin/projects/${id}/${action}`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}
